import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import TierManageContentButton from '@wix/da-shared-react/pkg/Tier/TierManageContentButton';
import UnderlinedTabs from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SubscriptionsTierContentType } from '../../../../types/subscriptionsSection';
import TierContentFeed from './TierContentFeed';
import TiersDropdown from './TiersDropdown';
import EmptyState from './EmptyState';

import s from './TierContent.scss';

export interface Props {
  isOwner: boolean;
  profileOwner: PapiUser;
  moduleId: number;
  streamId: string;
  tier?: PapiDeviation;
  isEmpty?: boolean;
  selectedTierContentType: SubscriptionsTierContentType;
  setSelectedTierContentType: (type: SubscriptionsTierContentType) => void;
}

const TierContent: React.FC<Props> = ({
  isEmpty,
  isOwner,
  profileOwner,
  tier,
  streamId,
  selectedTierContentType,
  setSelectedTierContentType,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const tabs = useMemo(
    () => [
      {
        index: SubscriptionsTierContentType.DEVIATIONS,
        label: t('widgets.tierContent.tabs.deviations'),
      },
      {
        index: SubscriptionsTierContentType.POSTS,
        label: t('widgets.tierContent.tabs.posts'),
      },
    ],
    [t]
  );

  if (!tier) {
    return null;
  }

  const renderManageContentButtons = () => (
    <>
      <TierManageContentButton
        type={'deviations'}
        tierDeviation={tier}
        profileOwner={profileOwner}
      />
      <TierManageContentButton
        type={'posts'}
        tierDeviation={tier}
        profileOwner={profileOwner}
      />
    </>
  );

  return (
    <div className={s['root']}>
      <div className={s['toolbar']}>
        {!isMobile && <TiersDropdown className={s['title']} />}
        <UnderlinedTabs
          size="big"
          className={s['tabs']}
          items={tabs}
          activeIndex={selectedTierContentType}
          onChange={tab =>
            setSelectedTierContentType(tab as SubscriptionsTierContentType)
          }
        />
        {!isMobile && (
          <div className={s['right-side-area']}>
            {isOwner && tier.isPublished && renderManageContentButtons()}
          </div>
        )}
      </div>
      <div className={s['content']}>
        {!tier.isPublished && (
          <EmptyState isDraft tierContentType={selectedTierContentType} />
        )}
        {tier.isPublished && (
          <TierContentFeed
            tier={tier}
            streamId={streamId}
            tierContentType={selectedTierContentType}
          />
        )}
      </div>
    </div>
  );
};
TierContent.displayName = 'TierContent';

export default TierContent;
